import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const mainIndex = this.#getShopWindowMainIndex(newMediaGallery);
    if (mainIndex > 0) {
      // slideshow.js now keeps current at 0 until select() runs, so this will scroll.
      newMediaGallery.querySelector('slideshow-component')?.setAttribute('initial-slide', String(mainIndex));
    }

    this.replaceWith(newMediaGallery);
    this.#selectShopWindowMainSlide(newMediaGallery);
  };

  /**
   * After variant switch: show metafield first image as the main slide.
   * Video remains at gallery index 0.
   *
   * @param {Element} gallery
   */
  #selectShopWindowMainSlide(gallery) {
    const mainIndex = this.#getShopWindowMainIndex(gallery);
    if (mainIndex < 1) return;

    const trySelect = (attempts = 0) => {
      const slideshow = /** @type {any} */ (gallery.querySelector('slideshow-component'));
      const slides = slideshow?.refs?.slides || slideshow?.querySelectorAll?.('slideshow-slide');
      const scroller = slideshow?.refs?.scroller || slideshow?.querySelector?.('slideshow-slides');
      const targetSlide = slides?.[mainIndex];

      if (slideshow && targetSlide && slides?.length > mainIndex) {
        // select() returns early when current === target without scrolling.
        if (slideshow.current === mainIndex) {
          slideshow.current = 0;
        }

        if (typeof slideshow.select === 'function') {
          slideshow.select(mainIndex, undefined, { animate: false });
        }

        // Hard fallback: force scroll position to the metafield image slide.
        if (scroller instanceof HTMLElement) {
          const left = targetSlide.offsetLeft;
          if (typeof scroller.scrollTo === 'function') {
            scroller.scrollTo({ left, top: 0, behavior: 'instant' });
          } else {
            scroller.scrollLeft = left;
          }
        }

        this.#syncControls(gallery, mainIndex);

        const scrolled =
          !(scroller instanceof HTMLElement) ||
          Math.abs(scroller.scrollLeft - targetSlide.offsetLeft) < 8 ||
          attempts >= 40;

        if (scrolled) return;
      }

      if (attempts < 40) {
        requestAnimationFrame(() => trySelect(attempts + 1));
      }
    };

    // Wait for slideshow connectedCallback + scroll setup.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => trySelect());
    });
    setTimeout(() => trySelect(), 100);
    setTimeout(() => trySelect(), 300);
  }

  /**
   * @param {Element} gallery
   * @param {number} mainIndex
   */
  #syncControls(gallery, mainIndex) {
    gallery.querySelectorAll('slideshow-slide').forEach((slide, index) => {
      slide.setAttribute('aria-hidden', index === mainIndex ? 'false' : 'true');
    });

    gallery.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumb, index) => {
      if (index === mainIndex) {
        thumb.setAttribute('aria-selected', 'true');
      } else {
        thumb.removeAttribute('aria-selected');
      }
    });

    gallery.querySelectorAll('[ref="dots[]"]').forEach((dot, index) => {
      if (index === mainIndex) {
        dot.setAttribute('aria-selected', 'true');
      } else {
        dot.removeAttribute('aria-selected');
      }
    });

    const counter = gallery.querySelector('.slideshow-controls__counter [ref="current"]');
    if (counter) counter.textContent = String(mainIndex + 1);
  }

  /**
   * @param {Element} gallery
   * @returns {number}
   */
  #getShopWindowMainIndex(gallery) {
    const mainIndexAttr = gallery.getAttribute('data-shop-window-main-index');
    if (mainIndexAttr != null && mainIndexAttr !== '') {
      const mainIndex = Number.parseInt(mainIndexAttr, 10);
      if (!Number.isNaN(mainIndex)) return mainIndex;
    }

    // Fallback: first image slide after a leading video
    const slides = gallery.querySelectorAll('slideshow-slide');
    if (!slides.length) return -1;

    const firstIsVideo =
      slides[0].classList.contains('product-media-container--video') ||
      slides[0].classList.contains('product-media-container--external_video');

    if (!firstIsVideo) return -1;

    for (let i = 1; i < slides.length; i++) {
      if (slides[i].classList.contains('product-media-container--image')) return i;
    }

    return slides.length > 1 ? 1 : -1;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
